import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Plus, Pencil, Trash2, Loader, X, Search, BookOpen,
  Clock, User, Globe, ImageOff, Upload, Eye, GraduationCap,
  FileText, Video, File, ChevronDown, ChevronRight,
} from "lucide-react";
import {
  fetchLmsCourses,
  fetchLmsSchoolClasses,
  fetchLmsClassSubjects,
} from "../redux/lmsSlice";
import {
  lmsCreateCourse,
  lmsUpdateCourse,
  lmsDeleteCourse,
  lmsUploadCourseThumbnail,
  lmsGetTeachers,
  lmsGetCourseTeachers,
  lmsAssignCourseTeacher,
  lmsRemoveCourseTeacher,
} from "../api/lmsApi";
import "../lms.css";

/* ── helpers ── */
const getSchoolId = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}").admin_id || null; }
  catch { return null; }
};

const LEVELS = ["beginner", "intermediate", "advanced"];
const LEVEL_COLORS = {
  beginner:     { bg: "#dcfce7", color: "#166534" },
  intermediate: { bg: "#fef9c3", color: "#854d0e" },
  advanced:     { bg: "#fee2e2", color: "#991b1b" },
};

function mkForm() {
  return {
    name: "", description: "",
    price: "", discount_price: "", duration_days: "",
    level: "beginner", language: "English",
    instructor_name: "", is_free: false,
    class_id: "", subject_id: "",
  };
}

/* ── Confirm Modal ── */
function ConfirmModal({ message, onConfirm, onCancel, loading }) {
  return (
    <div className="ms2-modal-overlay" onClick={onCancel}>
      <div className="ms2-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms2-confirm-icon"><Trash2 size={24} color="#dc2626" /></div>
        <p className="ms2-confirm-msg">
          {message.split(/("([^"]+)")/g).map((part, i) =>
            part.startsWith('"') && part.endsWith('"')
              ? <strong key={i} style={{ color: "#4f46e5", fontWeight: 700 }}>{part}</strong>
              : part
          )}
        </p>
        <div className="ms2-confirm-footer">
          <button className="lms-btn-ghost" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="ms2-confirm-delete-btn" onClick={onConfirm} disabled={loading}>
            {loading ? <Loader size={13} className="ms2-spin" /> : <Trash2 size={13} />} Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Course Form Modal ── */
function CourseModal({ course, schoolId, classes, classSubjects, onClose, onSaved }) {
  const thumbInputRef = useRef(null);
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    lmsGetTeachers({ school_id: schoolId })
      .then((res) => setTeachers(res.data.data || []))
      .catch(() => {});
  }, [schoolId]);

  const [form, setForm] = useState(course ? {
    name: course.name || "", description: course.description || "",
    price: course.price ?? "", discount_price: course.discount_price ?? "",
    duration_days: course.duration_days ?? "",
    level: course.level || "beginner", language: course.language || "English",
    instructor_name: course.instructor_name || "",
    is_free: !!course.is_free,
    class_id: course.class_id ?? "", subject_id: course.subject_id ?? "",
  } : mkForm());
  const [thumbFile, setThumbFile]   = useState(null);
  const [thumbPreview, setThumbPreview] = useState(course?.thumbnail_url || null);
  const [saving, setSaving]         = useState(false);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const canSave = form.name.trim();

  // Filter classSubjects by selected class (compare as strings to avoid type mismatch)
  const subjectOptions = form.class_id
    ? (classSubjects || []).filter((s) => String(s.class_id) === String(form.class_id))
    : [];

  const handleThumbPick = (files) => {
    if (!files || !files[0]) return;
    const file = files[0];
    setThumbFile(file);
    setThumbPreview(URL.createObjectURL(file));
  };

  const removeThumb = () => {
    setThumbFile(null);
    setThumbPreview(null);
    if (thumbInputRef.current) thumbInputRef.current.value = "";
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      // Upload thumbnail first if a new file was picked
      let thumbnail_url = course?.thumbnail_url || null;
      if (thumbFile) {
        const res = await lmsUploadCourseThumbnail(thumbFile);
        thumbnail_url = res.data.thumbnail_url;
      }

      const payload = {
        school_id: schoolId,
        name: form.name.trim(),
        description: form.description.trim() || null,
        price: form.is_free ? 0 : (parseFloat(form.price) || 0),
        discount_price: form.discount_price ? parseFloat(form.discount_price) : null,
        duration_days: form.duration_days ? parseInt(form.duration_days) : null,
        level: form.level,
        language: form.language || "English",
        instructor_name: form.instructor_name.trim() || null,
        is_free: form.is_free ? 1 : 0,
        class_id: form.class_id || null,
        subject_id: form.subject_id || null,
        thumbnail_url,
      };

      if (course) {
        await lmsUpdateCourse(course.id, payload);
        toast.success("Course updated");
      } else {
        await lmsCreateCourse(payload);
        toast.success("Course created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save course");
    } finally { setSaving(false); }
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="crs-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="crs-modal-header">
          <h2>{course ? "Edit Course" : "Create New Course"}</h2>
          <button className="crs-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="crs-modal-body">

          {/* Grid 2-col: name + instructor */}
          <div className="crs-grid2">
            <div className="crs-field">
              <label>Course Name <span className="crs-req">*</span></label>
              <input type="text" placeholder="e.g. Complete Mathematics" value={form.name}
                onChange={(e) => set("name", e.target.value)} autoFocus />
            </div>
            <div className="crs-field">
              <label>Instructor</label>
              <select value={form.instructor_name} onChange={(e) => set("instructor_name", e.target.value)}>
                <option value="">— Select teacher —</option>
                {teachers.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}{t.specialization ? ` (${t.specialization})` : ""}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Full: description */}
          <div className="crs-field">
            <label>Description</label>
            <textarea placeholder="Course description..." rows={3} value={form.description}
              onChange={(e) => set("description", e.target.value)} />
          </div>

          {/* Grid 2-col: class + subject */}
          <div className="crs-grid2">
            <div className="crs-field">
              <label>Class</label>
              <select value={form.class_id} onChange={(e) => {
                set("class_id", e.target.value);
                set("subject_id", ""); // reset subject when class changes
              }}>
                <option value="">— Select class —</option>
                {classes.map((c) => <option key={c.class_id ?? c.id} value={c.class_id ?? c.id}>{c.class_name ?? c.name}</option>)}
              </select>
            </div>
            <div className="crs-field">
              <label>Subject</label>
              <select value={form.subject_id} onChange={(e) => set("subject_id", e.target.value)}
                disabled={!form.class_id}>
                <option value="">— Select subject —</option>
                {subjectOptions.map((s) => (
                  <option key={s.id} value={s.subject_id || s.id}>{s.subject_name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Grid 3-col: level + language + duration */}
          <div className="crs-grid3">
            <div className="crs-field">
              <label>Level</label>
              <select value={form.level} onChange={(e) => set("level", e.target.value)}>
                {LEVELS.map((l) => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
              </select>
            </div>
            <div className="crs-field">
              <label>Language</label>
              <input type="text" placeholder="English" value={form.language}
                onChange={(e) => set("language", e.target.value)} />
            </div>
            <div className="crs-field">
              <label>Duration (days)</label>
              <input type="number" placeholder="e.g. 30" min="0" value={form.duration_days}
                onChange={(e) => set("duration_days", e.target.value)} />
            </div>
          </div>

          <hr className="crs-divider" />

          {/* Free toggle */}
          <div className="crs-toggle-row">
            <label className="crs-switch">
              <input type="checkbox" checked={form.is_free} onChange={(e) => set("is_free", e.target.checked)} />
              <span className="crs-slider" />
            </label>
            <span className="crs-toggle-label" onClick={() => set("is_free", !form.is_free)}>Free course</span>
          </div>

          {/* Grid 2-col: price + discount — dimmed when free */}
          <div className={`crs-grid2 crs-price-section${form.is_free ? " crs-price-disabled" : ""}`}>
            <div className="crs-field">
              <label>Price (₹) <span className="crs-req">*</span></label>
              <input type="number" placeholder="e.g. 499.00" min="0" step="0.01"
                value={form.price} onChange={(e) => set("price", e.target.value)}
                disabled={form.is_free} />
            </div>
            <div className="crs-field">
              <label>Discount Price (₹)</label>
              <input type="number" placeholder="e.g. 299.00" min="0" step="0.01"
                value={form.discount_price} onChange={(e) => set("discount_price", e.target.value)}
                disabled={form.is_free} />
            </div>
          </div>

          {/* Thumbnail upload */}
          <div className="crs-field">
            <label>Thumbnail Image</label>
            {thumbPreview ? (
              <div className="crs-thumb-preview">
                <img src={thumbPreview} alt="thumbnail preview" />
                <button className="crs-thumb-remove" onClick={removeThumb}>✕</button>
              </div>
            ) : (
              <div className="crs-upload-box" onClick={() => thumbInputRef.current?.click()}>
                <Upload size={26} color="#4f46e5" />
                <p className="crs-upload-text">Click to upload thumbnail</p>
                <p className="crs-upload-hint">JPG, PNG, WEBP recommended — 16:9 ratio</p>
              </div>
            )}
            <input ref={thumbInputRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={(e) => handleThumbPick(e.target.files)} />
          </div>

        </div>

        {/* Footer */}
        <div className="crs-modal-footer">
          <button className="crs-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="crs-btn-save" onClick={handleSave} disabled={saving || !canSave}>
            {saving ? <Loader size={13} className="ms2-spin" /> : "+ "}
            {course ? "Save Changes" : "Create Course"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Course View Modal ── */
function CourseViewModal({ course, onClose, onEdit }) {
  const lvl = LEVEL_COLORS[course.level] || LEVEL_COLORS.beginner;
  const price = parseFloat(course.price) || 0;
  const discountPrice = parseFloat(course.discount_price);

  const Row = ({ label, value }) => value != null && value !== "" ? (
    <div className="crs-view-row">
      <span className="crs-view-label">{label}</span>
      <span className="crs-view-value">{value}</span>
    </div>
  ) : null;

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="crs-view-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crs-view-header">
          <span>Course Details</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="crs-view-body">
          {/* Level + Free badge */}
          <div className="crs-view-badges">
            <span className="crs-level-badge" style={{ background: lvl.bg, color: lvl.color, position: "relative", fontSize: 12, padding: "3px 10px", borderRadius: 20 }}>
              {course.level || "beginner"}
            </span>
            {!!course.is_free && <span className="crs-free-badge" style={{ position: "relative", fontSize: 12, padding: "3px 10px", borderRadius: 20 }}>FREE</span>}
          </div>

          <h2 className="crs-view-title">{course.name}</h2>
          {course.description && <p className="crs-view-desc">{course.description}</p>}

          <div className="crs-view-details">
            <Row label="Instructor"    value={course.instructor_name} />
            <Row label="Language"      value={course.language} />
            <Row label="Duration"      value={course.duration_days ? `${course.duration_days} days` : null} />
            <Row label="Level"         value={course.level} />
            <Row label="Class"         value={course.class_name} />
            <Row label="Subject"       value={course.subject_name} />
            <Row label="Price"         value={course.is_free ? "Free" : price > 0 ? `₹${price.toFixed(2)}` : null} />
            <Row label="Discount Price" value={!course.is_free && discountPrice ? `₹${discountPrice.toFixed(2)}` : null} />
            <Row label="Created"       value={course.created_at ? new Date(course.created_at).toLocaleDateString() : null} />
          </div>
        </div>

        <div className="crs-view-footer">
          <button className="crs-btn-cancel" onClick={onClose}>Close</button>
          <button className="crs-btn-save" onClick={() => { onClose(); onEdit(course); }}>
            <Pencil size={13} /> Edit Course
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Material type icon helper ── */
function MatIcon({ type, docType }) {
  if (type === "video" || type === "video_link")
    return <Video size={13} style={{ color: "#7c3aed", flexShrink: 0 }} />;
  if (docType === "pdf" || (type || "").includes("pdf"))
    return <FileText size={13} style={{ color: "#dc2626", flexShrink: 0 }} />;
  return <File size={13} style={{ color: "#0ea5e9", flexShrink: 0 }} />;
}


/* ── Course Teachers Modal ── */
function CourseTeachersModal({ course, schoolId, onClose }) {
  const [allTeachers, setAllTeachers] = useState([]);
  const [assigned, setAssigned]       = useState([]); // array of teacher_id
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);

  useEffect(() => {
    Promise.all([
      lmsGetTeachers({ school_id: schoolId }),
      lmsGetCourseTeachers(course.id),
    ]).then(([tRes, ctRes]) => {
      setAllTeachers(tRes.data.data || []);
      setAssigned((ctRes.data.data || []).map((ct) => ct.teacher_id));
    }).catch(() => toast.error("Failed to load teachers"))
      .finally(() => setLoading(false));
  }, [course.id, schoolId]);

  const toggle = async (teacherId) => {
    setSaving(true);
    try {
      if (assigned.includes(teacherId)) {
        await lmsRemoveCourseTeacher(course.id, teacherId);
        setAssigned((p) => p.filter((x) => x !== teacherId));
        toast.success("Teacher removed from course");
      } else {
        await lmsAssignCourseTeacher(course.id, { teacher_id: teacherId, school_id: schoolId });
        setAssigned((p) => [...p, teacherId]);
        toast.success("Teacher assigned to course");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    } finally { setSaving(false); }
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div style={{
        width: "100%", maxWidth: 560, background: "#fff", borderRadius: 12,
        boxShadow: "0 20px 40px rgba(0,0,0,0.15)", display: "flex",
        flexDirection: "column", maxHeight: "85vh", overflow: "hidden",
      }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          padding: "18px 20px", borderBottom: "1px solid #e5e7eb",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Manage Teachers</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {course.name} &nbsp;·&nbsp;
              <span style={{ color: "#4f46e5", fontWeight: 600 }}>{assigned.length} assigned</span>
            </div>
          </div>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Loader size={24} className="ms2-spin" />
            </div>
          ) : allTeachers.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: "#9ca3af", fontSize: 14 }}>
              No teachers found. Add teachers in the Teachers section first.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {allTeachers.map((t) => {
                const isAssigned = assigned.includes(t.id);
                const initials = t.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
                return (
                  <div key={t.id} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "12px 14px", borderRadius: 10,
                    border: `1.5px solid ${isAssigned ? "#4f46e5" : "#e5e7eb"}`,
                    background: isAssigned ? "#eef2ff" : "#fafafa",
                    transition: "all 0.15s",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      {t.profile_photo ? (
                        <img src={t.profile_photo} alt={t.name} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                      ) : (
                        <div style={{
                          width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                          background: isAssigned ? "#c7d2fe" : "#e5e7eb",
                          color: isAssigned ? "#4338ca" : "#6b7280",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, fontSize: 14,
                        }}>{initials}</div>
                      )}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{t.name}</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          {t.specialization || t.qualification || "Teacher"}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggle(t.id)}
                      disabled={saving}
                      style={{
                        padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                        cursor: saving ? "not-allowed" : "pointer",
                        border: `1.5px solid ${isAssigned ? "#dc2626" : "#4f46e5"}`,
                        background: isAssigned ? "#fff" : "#4f46e5",
                        color: isAssigned ? "#dc2626" : "#fff",
                        display: "flex", alignItems: "center", gap: 4,
                        transition: "all 0.15s",
                      }}
                    >
                      {isAssigned ? <><X size={11} /> Remove</> : <><Plus size={11} /> Assign</>}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 20px", borderTop: "1px solid #e5e7eb",
          display: "flex", justifyContent: "flex-end", flexShrink: 0,
        }}>
          <button className="crs-btn-cancel" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

/* ── Course Card ── */
function CourseCard({ course, onEdit, onDelete, onNavigate }) {
  const lvl = LEVEL_COLORS[course.level] || LEVEL_COLORS.beginner;
  const price = parseFloat(course.price) || 0;
  const discountPrice = parseFloat(course.discount_price);

  return (
    <div className="crs-card" onClick={() => onNavigate(course.id)} style={{ cursor: "pointer" }}>
      {/* Thumbnail */}
      <div className="crs-card-thumb">
        {course.thumbnail_url
          ? <img src={course.thumbnail_url} alt={course.name} onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
          : null}
        <div className="crs-card-thumb-placeholder" style={{ display: course.thumbnail_url ? "none" : "flex" }}>
          <ImageOff size={28} color="#9ca3af" />
        </div>
        <span className="crs-level-badge" style={{ background: lvl.bg, color: lvl.color }}>
          {course.level || "beginner"}
        </span>
        {course.is_free ? (
          <span className="crs-free-badge">FREE</span>
        ) : null}
      </div>

      {/* Body */}
      <div className="crs-card-body">
        <h3 className="crs-card-title">{course.name}</h3>
        {course.description && <p className="crs-card-desc">{course.description}</p>}

        <div className="crs-card-meta">
          {course.instructor_name && (
            <span className="crs-meta-item"><User size={12} />{course.instructor_name}</span>
          )}
          {course.duration_days && (
            <span className="crs-meta-item"><Clock size={12} />{course.duration_days} days</span>
          )}
          {course.language && (
            <span className="crs-meta-item"><Globe size={12} />{course.language}</span>
          )}
        </div>

        {/* Price */}
        <div className="crs-card-price">
          {course.is_free ? (
            <span className="crs-price-free">Free</span>
          ) : (
            <>
              {discountPrice ? (
                <>
                  <span className="crs-price-discounted">₹{discountPrice.toFixed(2)}</span>
                  <span className="crs-price-original">₹{price.toFixed(2)}</span>
                </>
              ) : (
                <span className="crs-price-main">{price > 0 ? `₹${price.toFixed(2)}` : "Free"}</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="crs-card-actions" onClick={(e) => e.stopPropagation()}>

<button className="crs-action-btn crs-action-edit" onClick={(e) => { e.stopPropagation(); onEdit(course); }} title="Edit">
          <Pencil size={14} />
        </button>
        <button className="crs-action-btn crs-action-delete" onClick={(e) => { e.stopPropagation(); onDelete(course); }} title="Delete">
          <Trash2 size={14} />
        </button>
      </div>

    </div>
  );
}

/* ══════════════════════════════════════════
   COURSES PAGE
══════════════════════════════════════════ */
export default function Courses() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { courses, coursesLoading, schoolClasses, classSubjects } = useSelector((s) => s.lms);

  const schoolId = getSchoolId();

  const [search, setSearch]         = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [filterFree, setFilterFree]   = useState("");
  const [showModal, setShowModal]   = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [confirm, setConfirm]       = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchLmsCourses({ school_id: schoolId }));
    dispatch(fetchLmsSchoolClasses(schoolId));
    dispatch(fetchLmsClassSubjects({ school_id: schoolId }));
  }, [dispatch, schoolId]);

  const refresh = () => dispatch(fetchLmsCourses({ school_id: schoolId }));

  const handleEdit = (course) => { setEditCourse(course); setShowModal(true); };
  const handleDelete = (course) => {
    setConfirm({
      message: `Are you sure you want to delete the course "${course.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        await lmsDeleteCourse(course.id);
        toast.success("Course deleted");
        refresh();
      },
    });
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try { await confirm.onConfirm(); }
    finally { setConfirmLoading(false); setConfirm(null); }
  };

  /* filtering */
  const filtered = courses.filter((c) => {
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterLevel && c.level !== filterLevel) return false;
    if (filterFree === "free" && !c.is_free) return false;
    if (filterFree === "paid" && c.is_free) return false;
    return true;
  });

  return (
    <div className="crs-root">
      {/* Header */}
      <div className="lms-page-header">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 className="lms-page-title">Courses</h1>
            <span style={{ fontSize: 13, fontWeight: 700, background: "#eef2ff", color: "#4f46e5",
              padding: "3px 12px", borderRadius: 20 }}>
              {courses.length} course{courses.length !== 1 ? "s" : ""}
            </span>
          </div>
          <p className="lms-page-subtitle">Manage all courses for your school</p>
        </div>
        <button className="lms-btn-primary" onClick={() => { setEditCourse(null); setShowModal(true); }}>
          <Plus size={15} /> Create Course
        </button>
      </div>

      {/* Filters */}
      <div className="crs-filters">
        <div className="crs-search-wrap">
          <Search size={14} />
          <input placeholder="Search courses..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="crs-filter-select" value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
          <option value="">All Levels</option>
          {LEVELS.map((l) => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
        </select>
        <select className="crs-filter-select" value={filterFree} onChange={(e) => setFilterFree(e.target.value)}>
          <option value="">All Types</option>
          <option value="free">Free</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      {/* Grid */}
      {coursesLoading ? (
        <div className="crs-loading"><Loader size={28} className="ms2-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="crs-empty">
          <BookOpen size={40} color="#d1d5db" />
          <p>{search || filterLevel || filterFree ? "No courses match your filters." : "No courses yet. Click \"Create Course\" to get started."}</p>
        </div>
      ) : (
        <div className="crs-grid">
          {filtered.map((c) => (
            <CourseCard key={c.id} course={c} onEdit={handleEdit} onDelete={handleDelete}
              onNavigate={(id) => navigate(`/lms/courses/${id}`)} />
          ))}
        </div>
      )}


      {/* Create / Edit modal */}
      {showModal && (
        <CourseModal
          course={editCourse}
          schoolId={schoolId}
          classes={schoolClasses}
          classSubjects={classSubjects}
          onClose={() => { setShowModal(false); setEditCourse(null); }}
          onSaved={refresh}
        />
      )}

      {/* Confirm delete */}
      {confirm && (
        <ConfirmModal
          message={confirm.message}
          onConfirm={runConfirmed}
          onCancel={() => setConfirm(null)}
          loading={confirmLoading}
        />
      )}
    </div>
  );
}
